import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button as MuiButton,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Link as MuiLink,
  Toolbar,
  Typography
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  FavoriteBorder as FavoriteBorderIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import BrandLogo from '../common/BrandLogo';
import { API_ENDPOINTS, HEADER_HEIGHT, ROUTES } from '../../constants';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { logout } from '../../features/authSlice';
import { AnnouncementBar, HeaderNavItem } from '../../types';

const fallbackNav: HeaderNavItem[] = [
  { id: 'home', label: 'Home', path: ROUTES.HOME, visible: true, position: 'left', order: 1 },
  { id: 'shop', label: 'Shop', path: ROUTES.PRODUCTS, visible: true, position: 'left', order: 2 },
  {
    id: 'under-599',
    label: 'Under ₹599',
    path: `${ROUTES.PRODUCTS}?maxPrice=599`,
    visible: true,
    position: 'left',
    order: 3
  }
];

const Header: React.FC = () => {
  const ANNOUNCEMENT_HEIGHT = 32;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [accountAnchor, setAccountAnchor] = React.useState<null | HTMLElement>(null);

  const handleLogout = () => {
    setAccountAnchor(null);
    dispatch(logout());
    navigate(ROUTES.HOME);
  };
  const [searchValue, setSearchValue] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const isAdminRoute = location.pathname.startsWith('/admin');
  const { user } = useAppSelector((state) => state.auth);
  const cartCount = useAppSelector((state) => {
    const serverCount = (state.cart.cart?.items || []).reduce((sum, item) => sum + item.quantity, 0);
    const guestCount = state.cart.guestItems.reduce((sum, item) => sum + item.quantity, 0);
    return serverCount + guestCount;
  });
  const wishlistCount = useAppSelector((state) => state.wishlist.wishlist?.items?.length || 0);
  const [navItems, setNavItems] = useState<HeaderNavItem[] | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeParentId, setActiveParentId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<Record<string, boolean>>({});
  const [announcementBar, setAnnouncementBar] = useState<AnnouncementBar | null>(null);

  const loadNav = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ items: HeaderNavItem[] }>(
        `${API_ENDPOINTS.SITE_SETTINGS}/header-nav`
      );
      if (Array.isArray(data.items) && data.items.length > 0) {
        setNavItems(data.items);
      } else {
        setNavItems(fallbackNav);
      }
    } catch (_error) {
      setNavItems(fallbackNav);
    }
  }, []);

  const loadAnnouncementBar = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ announcementBar: AnnouncementBar }>(
        `${API_ENDPOINTS.SITE_SETTINGS}/announcement-bar`
      );
      setAnnouncementBar(data.announcementBar || null);
    } catch (_error) {
      setAnnouncementBar(null);
    }
  }, []);

  useEffect(() => {
    loadNav();
    loadAnnouncementBar();
  }, [loadNav, loadAnnouncementBar]);

  useEffect(() => {
    const handleRefresh = () => {
      loadNav();
    };
    window.addEventListener('header-nav-updated', handleRefresh);
    return () => window.removeEventListener('header-nav-updated', handleRefresh);
  }, [loadNav]);

  useEffect(() => {
    const handleRefresh = () => {
      loadAnnouncementBar();
    };
    window.addEventListener('announcement-bar-updated', handleRefresh);
    return () => window.removeEventListener('announcement-bar-updated', handleRefresh);
  }, [loadAnnouncementBar]);

  const visibleItems = useMemo(
    () => (navItems || []).filter((item) => item.visible).sort((a, b) => a.order - b.order),
    [navItems]
  );

  const childrenByParent = useMemo(() => {
    const map: Record<string, HeaderNavItem[]> = {};
    visibleItems.forEach((item) => {
      if (!item.parentId) return;
      if (!map[item.parentId]) map[item.parentId] = [];
      map[item.parentId].push(item);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => a.order - b.order);
    });
    return map;
  }, [visibleItems]);

  const leftRoots = useMemo(
    () => visibleItems.filter((item) => item.position === 'left' && !item.parentId),
    [visibleItems]
  );
  const rightRoots = useMemo(
    () => visibleItems.filter((item) => item.position === 'right' && !item.parentId),
    [visibleItems]
  );

  const activeChildren = activeParentId ? childrenByParent[activeParentId] || [] : [];
  const visibleAnnouncements = useMemo(
    () =>
      (announcementBar?.items || [])
        .filter((item) => item.visible)
        .sort((a, b) => a.order - b.order),
    [announcementBar]
  );
  const showAnnouncementBar =
    !isAdminRoute && Boolean(announcementBar?.enabled && visibleAnnouncements.length > 0);
  const drawerTop = HEADER_HEIGHT + (showAnnouncementBar ? ANNOUNCEMENT_HEIGHT : 0);
  const tickerSpeedMs = announcementBar?.speedMs || 22000;
  const shouldAnimateTicker = visibleAnnouncements.length > 1;

  const normalizeNavPath = (path: string) => {
    const trimmedPath = path.trim();
    const underMatch = trimmedPath.match(/^\/?under-(\d+)$/i);
    if (underMatch) {
      return `${ROUTES.PRODUCTS}?maxPrice=${underMatch[1]}`;
    }
    return trimmedPath;
  };

  const renderNavItem = (item: HeaderNavItem) => {
    const hasChildren = (childrenByParent[item.id] || []).length > 0;
    const resolvedPath = normalizeNavPath(item.path);

    if (!hasChildren) {
      return (
        <MuiButton
          key={item.id}
          color="inherit"
          component={RouterLink}
          to={resolvedPath}
          target={item.openInNewTab ? '_blank' : undefined}
          rel={item.openInNewTab ? 'noreferrer' : undefined}
          sx={{
            px: 0.75,
            fontSize: '0.9rem',
            fontWeight: 500
          }}
        >
          {item.label}
        </MuiButton>
      );
    }

    return (
      <MuiButton
        key={item.id}
        color="inherit"
        sx={{ px: 0.75, fontSize: '0.9rem', fontWeight: 500 }}
        onMouseEnter={(event) => {
          setMenuAnchor(event.currentTarget);
          setActiveParentId(item.id);
        }}
      >
        {item.label}
      </MuiButton>
    );
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        height: showAnnouncementBar ? HEADER_HEIGHT + ANNOUNCEMENT_HEIGHT : HEADER_HEIGHT,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#fff',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      {showAnnouncementBar && (
        <Box
          sx={{
            height: ANNOUNCEMENT_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          {!shouldAnimateTicker ? (
            <Box sx={{ width: '100%', px: 2, textAlign: 'center' }}>
              {visibleAnnouncements[0].link ? (
                <MuiLink
                  component={RouterLink}
                  to={visibleAnnouncements[0].link || '#'}
                  underline="none"
                  color="inherit"
                  target={visibleAnnouncements[0].openInNewTab ? '_blank' : undefined}
                  rel={visibleAnnouncements[0].openInNewTab ? 'noreferrer' : undefined}
                  sx={{ fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.02em' }}
                >
                  {visibleAnnouncements[0].text}
                </MuiLink>
              ) : (
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                  {visibleAnnouncements[0].text}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                width: 'max-content',
                whiteSpace: 'nowrap',
                animation: `announcementTicker ${tickerSpeedMs}ms linear infinite`,
                '@keyframes announcementTicker': {
                  from: { transform: 'translateX(0)' },
                  to: { transform: 'translateX(-33.3333%)' }
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none'
                }
              }}
            >
              {[...visibleAnnouncements, ...visibleAnnouncements, ...visibleAnnouncements].map((item, index) => (
                <Box key={`${item.id}-${index}`} sx={{ px: 3, display: 'inline-flex', alignItems: 'center' }}>
                  {item.link ? (
                    <MuiLink
                      component={RouterLink}
                      to={item.link}
                      underline="none"
                      color="inherit"
                      target={item.openInNewTab ? '_blank' : undefined}
                      rel={item.openInNewTab ? 'noreferrer' : undefined}
                      sx={{ fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.02em' }}
                    >
                      {item.text}
                    </MuiLink>
                  ) : (
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                      {item.text}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      <Toolbar sx={{ gap: { xs: 1, sm: 2, md: 3 }, px: { xs: 1, sm: 2 }, minHeight: HEADER_HEIGHT }}>
        <IconButton
          color="inherit"
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          onClick={() => setMobileOpen((prev) => !prev)}
        >
          <MenuIcon />
        </IconButton>

        <BrandLogo size={26} />

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 2,
            flexGrow: 1,
            ml: 4
          }}
        >
          {leftRoots.map(renderNavItem)}
        </Box>

        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1.5 } }}>
          <Box
            component="form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              const q = searchValue.trim();
              if (q) {
                navigate(`${ROUTES.PRODUCTS}?q=${encodeURIComponent(q)}`);
                setSearchValue('');
              }
            }}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              bgcolor: 'rgba(0,0,0,0.04)',
              borderRadius: 999,
              px: 1.5,
              py: 0.25,
              mr: 1
            }}
          >
            <SearchIcon sx={{ fontSize: 20, color: 'text.secondary', mr: 0.5 }} />
            <input
              type="text"
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '0.875rem',
                width: 140,
                color: 'inherit'
              }}
            />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            {rightRoots.map(renderNavItem)}
          </Box>

          {user?.role === 'admin' && (
            <MuiButton color="inherit" component={RouterLink} to={ROUTES.ADMIN} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
              Admin
            </MuiButton>
          )}

          <IconButton
            color="inherit"
            size="small"
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            onClick={() => setMobileSearchOpen((prev) => !prev)}
          >
            <SearchIcon />
          </IconButton>

          <IconButton color="inherit" component={RouterLink} to={ROUTES.CART} size="small">
            <Badge badgeContent={cartCount} color="secondary">
              <CartIcon />
            </Badge>
          </IconButton>

          <IconButton color="inherit" component={RouterLink} to={ROUTES.WISHLIST} size="small">
            <Badge badgeContent={wishlistCount} color="secondary">
              <FavoriteBorderIcon />
            </Badge>
          </IconButton>

          {user ? (
            <IconButton
              color="inherit"
              size="small"
              onClick={(e) => setAccountAnchor(e.currentTarget)}
              aria-label="Account menu"
            >
              <PersonIcon />
            </IconButton>
          ) : (
            <IconButton color="inherit" component={RouterLink} to={ROUTES.LOGIN} size="small">
              <PersonIcon />
            </IconButton>
          )}
        </Box>

        <Menu
          anchorEl={accountAnchor}
          open={Boolean(accountAnchor)}
          onClose={() => setAccountAnchor(null)}
        >
          <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 600 }}>
            Hi, {user?.name?.split(' ')[0] || 'there'}
          </MenuItem>
          <Divider />
          <MenuItem
            component={RouterLink}
            to={ROUTES.ACCOUNT}
            onClick={() => setAccountAnchor(null)}
          >
            My account & orders
          </MenuItem>
          {user?.role === 'admin' && (
            <MenuItem
              component={RouterLink}
              to={ROUTES.ADMIN}
              onClick={() => setAccountAnchor(null)}
            >
              Admin panel
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            Logout
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor) && activeChildren.length > 0}
          onClose={() => {
            setMenuAnchor(null);
            setActiveParentId(null);
          }}
          MenuListProps={{
            onMouseLeave: () => {
              setMenuAnchor(null);
              setActiveParentId(null);
            }
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {activeChildren.map((child) => (
            <MenuItem
              key={child.id}
              component={RouterLink}
              to={normalizeNavPath(child.path)}
              onClick={() => {
                setMenuAnchor(null);
                setActiveParentId(null);
              }}
              target={child.openInNewTab ? '_blank' : undefined}
              rel={child.openInNewTab ? 'noreferrer' : undefined}
            >
              {child.label}
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>

      {mobileSearchOpen && (
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            const q = searchValue.trim();
            if (q) {
              navigate(`${ROUTES.PRODUCTS}?q=${encodeURIComponent(q)}`);
              setSearchValue('');
              setMobileSearchOpen(false);
            }
          }}
          sx={{
            display: { xs: 'flex', md: 'none' },
            px: 2,
            pb: 1,
            gap: 1,
            alignItems: 'center',
            borderTop: '1px solid rgba(0,0,0,0.06)'
          }}
        >
          <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <input
            type="text"
            placeholder="Search products..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            autoFocus
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: '0.9rem',
              flex: 1,
              color: 'inherit',
              padding: '8px 0'
            }}
          />
        </Box>
      )}

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 320 },
            top: drawerTop,
            height: `calc(100% - ${drawerTop}px)`,
            borderRadius: 0
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <BrandLogo size={22} linked={false} />
          </Box>
          <List disablePadding>
            {leftRoots.concat(rightRoots).map((item) => {
              const children = childrenByParent[item.id] || [];
              const hasChildren = children.length > 0;

              return (
                <Box key={item.id}>
                  <ListItemButton
                    component={hasChildren ? 'button' : RouterLink}
                    to={hasChildren ? undefined : normalizeNavPath(item.path)}
                    onClick={() => {
                      if (hasChildren) {
                        setMobileExpanded((prev) => ({
                          ...prev,
                          [item.id]: !prev[item.id]
                        }));
                      } else {
                        setMobileOpen(false);
                      }
                    }}
                  >
                    <ListItemText primary={item.label} />
                    {hasChildren &&
                      (mobileExpanded[item.id] ? <ExpandLess /> : <ExpandMore />)}
                  </ListItemButton>
                  {hasChildren && (
                    <Collapse in={Boolean(mobileExpanded[item.id])} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {children.map((child) => (
                          <ListItemButton
                            key={child.id}
                            component={RouterLink}
                            to={normalizeNavPath(child.path)}
                            sx={{ pl: 4 }}
                            onClick={() => setMobileOpen(false)}
                          >
                            <ListItemText primary={child.label} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
